<template>
  <div class="submission-text-editor">
    <!-- 상단 툴바 -->
    <div class="editor-toolbar">
      <button type="button" class="toolbar-btn" @click="insertTable" title="표 삽입">
        <span>표 삽입</span>
      </button>
      <button type="button" class="toolbar-btn" @click="insertFigure" title="그림 삽입">
        <span>그림 삽입</span>
      </button>
      <button type="button" class="toolbar-btn" @click="insertFootnote" title="주석 삽입">
        <span>주석 삽입</span>
      </button>
      <button type="button" class="toolbar-btn" @click="showHelp = true" title="사용법">
        <span>사용법</span>
      </button>
      <div class="toolbar-spacer"></div>
      <button type="button" class="toolbar-btn primary" @click="convertAndPreview" title="미리보기">
        <span>미리보기</span>
      </button>
    </div>

    <!-- 에디터 본체 -->
    <div class="editor-body" :class="{ 'preview-mode': showPreview }">
      <!-- 텍스트 입력 영역 -->
      <div class="input-panel">
        <div class="panel-header">원본 텍스트 입력</div>
        <textarea
          ref="textInput"
          v-model="inputText"
          class="text-input"
          :placeholder="placeholderText"
          @input="onInputChange"
        ></textarea>
      </div>

      <!-- 미리보기 영역 -->
      <div v-if="showPreview" class="preview-panel">
        <div class="panel-header">
          변환 결과
          <button type="button" class="close-preview" @click="showPreview = false">✕</button>
        </div>
        <div class="preview-content" v-html="previewHtml"></div>
      </div>
    </div>

    <!-- 표 삽입 다이얼로그 -->
    <div v-if="showTableDialog" class="modal-overlay" @click.self="showTableDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>표 삽입</h3>
          <button type="button" class="close-btn" @click="showTableDialog = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>표 번호</label>
            <input type="number" v-model="tableForm.number" min="1" />
          </div>
          <div class="form-group">
            <label>표 제목</label>
            <input type="text" v-model="tableForm.caption" placeholder="표 제목을 입력하세요" />
          </div>
          <div class="form-group">
            <label>행 수</label>
            <input type="number" v-model="tableForm.rows" min="1" max="20" />
          </div>
          <div class="form-group">
            <label>열 수</label>
            <input type="number" v-model="tableForm.cols" min="1" max="10" />
          </div>
          <div class="form-actions">
            <button type="button" class="btn-cancel" @click="showTableDialog = false">취소</button>
            <button type="button" class="btn-insert" @click="doInsertTable">삽입</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 그림 삽입 다이얼로그 -->
    <div v-if="showFigureDialog" class="modal-overlay" @click.self="showFigureDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>그림 삽입</h3>
          <button type="button" class="close-btn" @click="showFigureDialog = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>그림 번호</label>
            <input type="number" v-model="figureForm.number" min="1" />
          </div>
          <div class="form-group">
            <label>그림 제목</label>
            <input type="text" v-model="figureForm.caption" placeholder="그림 제목을 입력하세요" />
          </div>
          <div class="form-group">
            <label>이미지 파일</label>
            <input type="file" @change="onFigureFileChange" accept="image/*" />
          </div>
          <div v-if="figureForm.preview" class="image-preview">
            <img :src="figureForm.preview" alt="미리보기" />
          </div>
          <div class="form-actions">
            <button type="button" class="btn-cancel" @click="showFigureDialog = false">취소</button>
            <button type="button" class="btn-insert" @click="doInsertFigure">삽입</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 주석 삽입 다이얼로그 -->
    <div v-if="showFootnoteDialog" class="modal-overlay" @click.self="showFootnoteDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>주석 삽입</h3>
          <button type="button" class="close-btn" @click="showFootnoteDialog = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>주석 번호</label>
            <input type="number" v-model="footnoteForm.number" min="1" />
          </div>
          <p class="form-hint">본문에 <code>^{{ footnoteForm.number }})</code> 형식으로 삽입됩니다. 미리보기에서 <sup>{{ footnoteForm.number }})</sup> 위첨자로 표시됩니다.</p>
          <div class="form-actions">
            <button type="button" class="btn-cancel" @click="showFootnoteDialog = false">취소</button>
            <button type="button" class="btn-insert" @click="doInsertFootnote">삽입</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 사용법 모달 -->
    <div v-if="showHelp" class="modal-overlay" @click.self="showHelp = false">
      <div class="modal-content help-modal">
        <div class="modal-header">
          <h3>📖 사용법 안내</h3>
          <button type="button" class="close-btn" @click="showHelp = false">✕</button>
        </div>
        <div class="modal-body help-content">
          <h4>1. 논문 메타정보</h4>
          <pre>TITLE: 논문 제목
AUTHOR: 홍길동
ABSTRACT: English abstract...
초록: 한글 초록 내용...
KEYWORDS: 키워드1, 키워드2</pre>

          <h4>2. 장/절 제목 (자동 변환)</h4>
          <pre>1. 서론          → H3 (장)
1.1 선행 연구    → H4 (절)
1.1.1 국내 연구  → H5 (소절)</pre>

          <h4>3. 인용문</h4>
          <pre>> 인용문 첫째 줄
> 인용문 둘째 줄</pre>

          <h4>4. 표 삽입</h4>
          <pre>&lt;표 1&gt; 표 제목
| 헤더1 | 헤더2 | 헤더3 |
| 내용1 | 내용2 | 내용3 |</pre>

          <h4>5. 그림 삽입</h4>
          <pre>&lt;그림 1&gt; 그림 제목</pre>

          <h4>6. 리스트</h4>
          <pre>• 항목 1
- 항목 2
* 항목 3</pre>

          <h4>7. 주석 (위첨자)</h4>
          <pre>본문 내용을 작성하고^1) 주석을 추가합니다.
→ 미리보기에서 "내용을 작성하고<sup>1)</sup>" 형태로 표시</pre>

          <h4>8. 참고문헌</h4>
          <pre>참고문헌

홍길동(2020), 「논문 제목」, 『학술지명』...</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
export default {
  name: "SubmissionTextEditor",
  props: {
    value: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      inputText: this.value || "",
      previewHtml: "",
      showPreview: false,
      showTableDialog: false,
      showFigureDialog: false,
      showFootnoteDialog: false,
      showHelp: false,
      tableForm: {
        number: 1,
        caption: "",
        rows: 3,
        cols: 3,
      },
      figureForm: {
        number: 1,
        caption: "",
        file: null,
        preview: null,
      },
      footnoteForm: {
        number: 1,
      },
      placeholderText: `여기에 논문 텍스트를 입력하세요.

규칙:
• TITLE: 논문 제목
• AUTHOR: 저자명
• ABSTRACT: 영문 초록
• 초록: 한글 초록
• KEYWORDS: 주제어
• 1. / 2. 장 제목 (H3)
• 1.1 / 2.1 절 제목 (H4)
• 1.1.1 소절 제목 (H5)
• > 인용문
• <표 1> 표 제목
• <그림 1> 그림 제목
• ^1) 주석 (위첨자)
• 참고문헌`,
    };
  },
  watch: {
    value(newVal) {
      if (newVal !== this.inputText) {
        this.inputText = newVal || "";
      }
    },
  },
  methods: {
    onInputChange() {
      const html = this.convertTextToHtml(this.inputText);
      this.$emit("input", html);
      this.$emit("change", html);
      this.$emit("update-content", html);
    },

    convertAndPreview() {
      this.previewHtml = this.convertTextToHtml(this.inputText);
      this.showPreview = true;
    },

    escapeHtml(s) {
      return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    // 주석 마커 ^N) 를 위첨자로 변환 (data-footnote-id 속성 포함)
    convertFootnoteMarkers(text) {
      return text.replace(/\^(\d+)\)/g, '<sup data-footnote-id="$1">$1)</sup>');
    },

    // 헤딩 인식: 1. / 1.1 / 1.1.1 등
    parseHeading(line) {
      const trimmed = line.trim();
      const m = trimmed.match(/^(\d+(?:\.\d+)*\.?)\s+(.+)$/);
      if (!m) return null;

      const numbering = m[1];
      const title = m[2].trim();

      if (title.length === 0 || title.length > 100) return null;

      // 문장 끝 패턴 제외
      const sentenceEnd = /(\.|다\.?|니다\.?|요\.?|한다\.?|하였다\.?|\?|!|;)$/;
      if (sentenceEnd.test(title)) return null;

      const depth = numbering.replace(/\.$/, "").split(".").length;

      // depth 1 = H3 (장), depth 2 = H4 (절), depth 3 = H5 (소절), depth 4+ = H6
      let level = 3;
      if (depth === 2) level = 4;
      else if (depth === 3) level = 5;
      else if (depth >= 4) level = 6;

      return { level, numbering, title };
    },

    // 글머리표 인식
    parseBullet(line) {
      const m = line.trimStart().match(/^([•\-*])\s+(.+)$/);
      if (!m) return null;
      return { text: m[2].trim() };
    },

    // 표 파싱
    parseTableRows(lines, startIdx) {
      const rows = [];
      let i = startIdx;

      while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        if (trimmed === "") break;

        if (
          trimmed.match(/^<(표|그림)\s*\d+>/) ||
          trimmed.match(/^(\d+(?:\.\d+)*\.?)\s+.+$/) ||
          trimmed.startsWith(">") ||
          trimmed === "참고문헌" ||
          trimmed.match(/^[•\-*]\s+/)
        ) {
          break;
        }

        let cells = [];

        if (trimmed.includes("|")) {
          cells = trimmed
            .split("|")
            .map((c) => c.trim())
            .filter((c) => c !== "");
        } else if (line.includes("\t")) {
          cells = line
            .split("\t")
            .map((c) => c.trim())
            .filter((c) => c !== "");
        } else if (trimmed.match(/\s{2,}/)) {
          cells = trimmed
            .split(/\s{2,}/)
            .map((c) => c.trim())
            .filter((c) => c !== "");
        } else if (trimmed.length > 0) {
          cells = [trimmed];
        }

        if (cells.length > 0) {
          rows.push(cells);
        }
        i++;
      }

      return { rows, endIdx: i };
    },

    // 텍스트를 HTML로 변환
    convertTextToHtml(text) {
      if (!text || !text.trim()) return "";

      const lines = text.replace(/\r\n?/g, "\n").split("\n");
      const html = [];

      let title = "";
      let author = "";
      let abstractEn = "";
      let abstractKo = "";
      let keywords = "";
      let inReferences = false;
      let paragraphBuf = [];
      let inList = false;
      let inBlockquote = false;
      let blockquoteBuf = [];

      const flushParagraph = () => {
        if (paragraphBuf.length === 0) return;
        const pText = paragraphBuf.join(" ").trim();
        if (pText) {
          const escaped = this.escapeHtml(pText);
          const withFootnotes = this.convertFootnoteMarkers(escaped);
          html.push(`<p style="text-indent: 1em; text-align: justify; margin: 10px 0;">${withFootnotes}</p>`);
        }
        paragraphBuf = [];
      };

      const flushBlockquote = () => {
        if (blockquoteBuf.length === 0) return;
        const qText = blockquoteBuf.join("<br>");
        html.push(`<blockquote style="margin: 20px 0; padding: 16px 24px; background: #f9fafb; border-left: 4px solid #9ca3af; font-size: 13px; line-height: 1.7; color: #4b5563;">${qText}</blockquote>`);
        blockquoteBuf = [];
        inBlockquote = false;
      };

      const openList = () => {
        if (inList) return;
        flushParagraph();
        flushBlockquote();
        html.push("<ul style='margin: 12px 0 12px 28px;'>");
        inList = true;
      };

      const closeList = () => {
        if (!inList) return;
        html.push("</ul>");
        inList = false;
      };

      // 1차 패스: 메타데이터 추출
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        if (line.startsWith("TITLE:")) {
          title = line.substring(6).trim();
          lines[i] = "";
        } else if (line.startsWith("AUTHOR:")) {
          author = line.substring(7).trim();
          lines[i] = "";
        } else if (line.startsWith("ABSTRACT:")) {
          abstractEn = line.substring(9).trim();
          lines[i] = "";
        } else if (line.startsWith("초록:")) {
          abstractKo = line.substring(3).trim();
          lines[i] = "";
        } else if (line.startsWith("KEYWORDS:") || line.startsWith("주제어:")) {
          keywords = line.replace(/^(KEYWORDS:|주제어:)/, "").trim();
          lines[i] = "";
        }
      }

      // 타이틀 출력
      if (title) {
        html.push(`<div style="font-size: 22px; font-weight: 700; text-align: center; margin-bottom: 16px; line-height: 1.4;">${this.escapeHtml(title)}</div>`);
      }
      if (author) {
        html.push(`<div style="font-size: 14px; text-align: center; margin-bottom: 30px; color: #333;">${this.escapeHtml(author)}</div>`);
      }

      // 초록 출력
      if (abstractEn || abstractKo) {
        html.push('<div style="margin: 30px 0; padding: 20px; background: #fafafa; border-left: 3px solid #2563eb;">');
        if (abstractEn) {
          html.push('<div style="font-size: 14px; font-weight: 700; margin-bottom: 12px;">Abstract</div>');
          html.push(`<div style="font-size: 13px; line-height: 1.7; text-align: justify;">${this.escapeHtml(abstractEn)}</div>`);
        }
        if (abstractKo) {
          html.push('<div style="font-size: 14px; font-weight: 700; margin-top: 16px; margin-bottom: 12px;">초록</div>');
          html.push(`<div style="font-size: 13px; line-height: 1.7; text-align: justify;">${this.escapeHtml(abstractKo)}</div>`);
        }
        html.push("</div>");
      }

      // 키워드 출력
      if (keywords) {
        html.push(`<div style="margin: 20px 0; font-size: 13px; color: #555;"><strong style="color: #333;">주제어:</strong> ${this.escapeHtml(keywords)}</div>`);
      }

      // 2차 패스: 본문 처리
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        if (trimmed === "") {
          flushParagraph();
          closeList();
          flushBlockquote();
          continue;
        }

        // 페이지 나누기 (--- 또는 === 3개 이상)
        if (/^[-=]{3,}$/.test(trimmed)) {
          flushParagraph();
          closeList();
          flushBlockquote();
          html.push('<div class="page-break" style="page-break-after: always; break-after: page; height: 1px; margin: 30px 0; border-bottom: 1px dashed #ccc;"></div>');
          continue;
        }

        // 참고문헌 섹션 시작 (다양한 형식 지원)
        const isReferencesTitle = (
          trimmed === "참고문헌" ||
          trimmed === "참고 문헌" ||
          trimmed === "References" ||
          trimmed === "• 참고문헌" ||
          trimmed === "•참고문헌" ||
          /^참고\s*문헌\s*$/i.test(trimmed) ||
          /^references\s*$/i.test(trimmed)
        );
        if (isReferencesTitle) {
          console.log('[SubmissionTextEditor] Found references title:', trimmed);
          flushParagraph();
          closeList();
          flushBlockquote();
          inReferences = true;
          // 래퍼 없이 제목만 출력 (페이지 넘김 허용)
          html.push('<div class="references-title" style="margin-top: 50px; font-size: 18px; font-weight: 700; margin-bottom: 20px;">참고문헌</div>');
          continue;
        }

        // 참고문헌 항목 (래퍼 없이 개별 출력)
        if (inReferences) {
          html.push(`<p class="reference-item" style="margin: 8px 0; padding-left: 2em; text-indent: -2em; font-size: 13px; line-height: 1.6; page-break-inside: avoid;">${this.escapeHtml(trimmed)}</p>`);
          continue;
        }

        // 인용문
        if (trimmed.startsWith(">")) {
          closeList();
          flushParagraph();
          inBlockquote = true;
          blockquoteBuf.push(this.escapeHtml(trimmed.substring(1).trim()));
          continue;
        } else if (inBlockquote) {
          flushBlockquote();
        }

        // 표 - 다음 줄이 |로 시작하는 테이블 행일 때만 표로 인식
        const tableMatch = trimmed.match(/^<표\s*(\d+)>\s*(.*)$/);
        const nextLine = lines[i + 1]?.trim();
        if (tableMatch && nextLine && nextLine.startsWith('|')) {
          flushParagraph();
          closeList();
          const tableNum = tableMatch[1];
          const tableCaption = tableMatch[2] || "";

          const { rows, endIdx } = this.parseTableRows(lines, i + 1);
          i = endIdx - 1;

          html.push('<div style="margin: 24px 0;">');
          html.push('<table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd; font-size: 13px;">');
          html.push(`<caption style="padding: 12px; caption-side: top; color: #333; text-align: center; font-size: 13px; font-weight: 600;">&lt;표 ${tableNum}&gt; ${this.escapeHtml(tableCaption)}</caption>`);

          if (rows.length > 0) {
            const maxCols = Math.max(...rows.map((r) => r.length));

            html.push("<thead><tr>");
            for (let c = 0; c < maxCols; c++) {
              const cell = rows[0][c] || "";
              html.push(`<th style="padding: 10px 14px; border: 1px solid #ddd; text-align: left; background-color: #f5f5f5; font-weight: 600;">${this.escapeHtml(cell)}</th>`);
            }
            html.push("</tr></thead>");

            if (rows.length > 1) {
              html.push("<tbody>");
              for (let r = 1; r < rows.length; r++) {
                html.push("<tr>");
                for (let c = 0; c < maxCols; c++) {
                  const cell = rows[r][c] || "";
                  html.push(`<td style="padding: 10px 14px; border: 1px solid #ddd; text-align: left;">${this.escapeHtml(cell)}</td>`);
                }
                html.push("</tr>");
              }
              html.push("</tbody>");
            }
          }
          html.push("</table></div>");
          continue;
        }

        // 그림
        const figureMatch = trimmed.match(/^<그림\s*(\d+)>\s*(.*)$/);
        if (figureMatch) {
          flushParagraph();
          closeList();
          const figNum = figureMatch[1];
          const figCaption = figureMatch[2] || "";

          html.push('<div style="margin: 24px 0; text-align: center;">');
          html.push('<div style="display: inline-block; width: 80%; max-width: 500px; height: 200px; background: #f3f4f6; border: 2px dashed #d1d5db; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 14px;">[그림 영역]</div>');
          html.push(`<div style="margin-top: 10px; font-size: 13px; font-weight: 600; color: #333;">&lt;그림 ${figNum}&gt; ${this.escapeHtml(figCaption)}</div>`);
          html.push("</div>");
          continue;
        }

        // 글머리표
        const bullet = this.parseBullet(line);
        if (bullet) {
          openList();
          html.push(`<li style="margin: 6px 0;">${this.escapeHtml(bullet.text)}</li>`);
          continue;
        } else {
          closeList();
        }

        // 헤딩
        const heading = this.parseHeading(trimmed);
        if (heading) {
          flushParagraph();
          closeList();
          const styles = {
            3: "font-size: 16px; font-weight: 700; margin: 30px 0 12px; border: none; border-bottom: none;",
            4: "font-size: 14px; font-weight: 600; margin: 24px 0 10px; border: none; border-bottom: none;",
            5: "font-size: 13px; font-weight: 600; margin: 20px 0 8px; border: none; border-bottom: none;",
            6: "font-size: 12px; font-weight: 600; margin: 16px 0 6px; border: none; border-bottom: none;",
          };
          const style = styles[heading.level] || styles[6];
          html.push(`<h${heading.level} style="${style}">${this.escapeHtml(heading.numbering)} ${this.escapeHtml(heading.title)}</h${heading.level}>`);
          continue;
        }

        // 일반 본문
        paragraphBuf.push(trimmed);
      }

      // 마무리
      closeList();
      flushParagraph();
      flushBlockquote();

      // 래퍼 제거로 닫는 태그 불필요

      return html.join("\n");
    },

    // 표 삽입
    insertTable() {
      this.tableForm = {
        number: this.getNextTableNumber(),
        caption: "",
        rows: 3,
        cols: 3,
      };
      this.showTableDialog = true;
    },

    getNextTableNumber() {
      const matches = this.inputText.match(/<표\s*(\d+)>/g);
      if (!matches) return 1;
      const numbers = matches.map((m) => parseInt(m.match(/\d+/)[0]));
      return Math.max(...numbers) + 1;
    },

    doInsertTable() {
      const { number, caption, rows, cols } = this.tableForm;
      let tableText = `<표 ${number}> ${caption}\n`;

      // 헤더 행
      const headers = [];
      for (let c = 0; c < cols; c++) {
        headers.push(`헤더${c + 1}`);
      }
      tableText += `| ${headers.join(" | ")} |\n`;

      // 데이터 행
      for (let r = 0; r < rows - 1; r++) {
        const cells = [];
        for (let c = 0; c < cols; c++) {
          cells.push(`내용`);
        }
        tableText += `| ${cells.join(" | ")} |\n`;
      }

      this.insertAtCursor(tableText + "\n");
      this.showTableDialog = false;
    },

    // 그림 삽입
    insertFigure() {
      this.figureForm = {
        number: this.getNextFigureNumber(),
        caption: "",
        file: null,
        preview: null,
      };
      this.showFigureDialog = true;
    },

    getNextFigureNumber() {
      const matches = this.inputText.match(/<그림\s*(\d+)>/g);
      if (!matches) return 1;
      const numbers = matches.map((m) => parseInt(m.match(/\d+/)[0]));
      return Math.max(...numbers) + 1;
    },

    onFigureFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.figureForm.file = file;
        const reader = new FileReader();
        reader.onload = (evt) => {
          this.figureForm.preview = evt.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    doInsertFigure() {
      const { number, caption } = this.figureForm;
      const figureText = `<그림 ${number}> ${caption}\n\n`;
      this.insertAtCursor(figureText);
      this.showFigureDialog = false;

      // TODO: 실제 이미지 업로드 처리
    },

    // 주석 삽입
    insertFootnote() {
      this.footnoteForm = {
        number: this.getNextFootnoteNumber(),
      };
      this.showFootnoteDialog = true;
    },

    getNextFootnoteNumber() {
      // ^N) 패턴에서 번호 추출
      const matches = this.inputText.match(/\^(\d+)\)/g);
      if (!matches) return 1;
      const numbers = matches.map((m) => parseInt(m.match(/\d+/)[0]));
      return Math.max(...numbers) + 1;
    },

    doInsertFootnote() {
      const { number } = this.footnoteForm;
      const footnoteText = `^${number})`;
      this.insertAtCursor(footnoteText);
      this.showFootnoteDialog = false;
    },

    insertAtCursor(text) {
      const textarea = this.$refs.textInput;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      this.inputText =
        this.inputText.substring(0, start) + text + this.inputText.substring(end);

      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + text.length;
        textarea.focus();
        this.onInputChange();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.submission-text-editor {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    border-color: #ccc;
  }

  &.primary {
    background: #2563eb;
    color: #fff;
    border-color: #2563eb;

    &:hover {
      background: #1d4ed8;
    }
  }
}

.toolbar-spacer {
  flex: 1;
}

.editor-body {
  display: grid;
  grid-template-columns: 1fr;

  &.preview-mode {
    grid-template-columns: 1fr 1fr;
  }
}

.input-panel,
.preview-panel {
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 10px 14px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 13px;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.close-preview {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  padding: 2px 6px;

  &:hover {
    color: #333;
  }
}

.text-input {
  flex: 1;
  min-height: 500px;
  padding: 16px;
  font-family: "D2Coding", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.6;
  border: none;
  resize: none;
  outline: none;
}

.preview-panel {
  border-left: 1px solid #e0e0e0;
}

.preview-content {
  flex: 1;
  min-height: 500px;
  padding: 30px 40px;
  overflow: auto;
  background: #fff;
  font-family: "Noto Serif KR", "Batang", Georgia, serif;
  line-height: 1.8;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  min-width: 400px;
  max-width: 600px;
  max-height: 80vh;
  overflow: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    margin: 0;
    font-size: 16px;
  }
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;

  label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #333;
  }

  input[type="text"],
  input[type="number"] {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #2563eb;
    }
  }
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin: 8px 0;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;

  code {
    background: #e0e0e0;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
  }

  sup {
    color: #1976d2;
    font-weight: bold;
  }
}

.form-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-cancel,
.btn-insert {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.btn-cancel {
  background: #fff;
  border: 1px solid #ddd;

  &:hover {
    background: #f5f5f5;
  }
}

.btn-insert {
  background: #2563eb;
  color: #fff;
  border: none;

  &:hover {
    background: #1d4ed8;
  }
}

.image-preview {
  margin-top: 12px;
  text-align: center;

  img {
    max-width: 100%;
    max-height: 200px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
}

/* 사용법 모달 */
.help-modal {
  min-width: 600px;
}

.help-content {
  h4 {
    font-size: 14px;
    color: #2563eb;
    margin: 20px 0 10px;

    &:first-child {
      margin-top: 0;
    }
  }

  pre {
    background: #f3f4f6;
    padding: 12px;
    border-radius: 6px;
    font-size: 12px;
    overflow-x: auto;
    font-family: "D2Coding", monospace;
  }
}
</style>
